/**
 * Settling a confirmed operation: how server truth reaches `base` before the pending overlay
 * drops.
 *
 * The overlay stops applying the moment its mutation leaves `pending`. If `base` doesn't already
 * reflect the write at that instant, the row snaps back to pre-mutation data and jumps forward
 * again when a refetch lands — so the settle has to run *before* the mutation resolves, and the
 * producer has to await it.
 *
 * Consumers were hand-rolling this and had already diverged on three details that are easy to get
 * wrong and invisible when wrong:
 *
 * - **Cancel first.** A fetch already in flight was issued against pre-mutation state; if it
 *   resolves after the fold it reinstates stale data.
 * - **Fold without advancing `dataUpdatedAt`.** The fold is a local write of a confirmed value,
 *   not a fresh read. Letting it bump the timestamp tells TanStack the data is newer than it is
 *   and suppresses the revalidation that would reconcile everything the operation doesn't carry.
 * - **Re-fold after revalidation.** A refetch that raced the mutation response can land between
 *   the fold and the resolve, reverting the confirmed value while the overlay is already gone.
 */
import type {QueryClient, QueryKey} from '@github-ui/react-query'

import {type ApplyOptions, applyOperation, collectIdentities, type ConnectionOperation} from './connection-operations'

export type SettleTarget = {
  queryKey: QueryKey
  /**
   * Passed through to `invalidateQueries`. Defaults to TanStack's `'active'`; use `'all'` when a
   * cache must be refetched even while nothing is subscribed to it — e.g. a list query behind an
   * open side panel, which would otherwise come back stale when the panel closes.
   */
  refetchType?: 'active' | 'inactive' | 'all'
  /**
   * Revalidate this key after folding, to reconcile server-derived fields the operation doesn't
   * carry (timestamps, counts, pagination). Awaited, so the overlay outlives it.
   */
  revalidate?: boolean
}

/**
 * Folds `operation` into each target's cached document, then optionally revalidates and folds
 * again. Returns a promise the producer must await before its mutation leaves `pending`.
 */
export async function settleConfirmedOperation({
  queryClient,
  targets,
  ownerId,
  operation,
  identityPath,
}: {
  queryClient: QueryClient
  targets: SettleTarget[]
  ownerId: string
  operation: ConnectionOperation
  /** Must match the identity the document's overlay uses, or the fold lands nowhere. */
  identityPath?: string[]
}): Promise<void> {
  if (targets.length === 0) return
  const applyOptions: ApplyOptions = identityPath ? {identityPath} : {}

  // Entity operations arrive from a bridge that deliberately doesn't filter by owner — a
  // `setFields` names its own target, so one mount serves every entity a document renders. That
  // means this runs for operations targeting entities this document has never heard of, and
  // cancelling and refetching for those would make every field edit anywhere in the app cost a
  // refetch here.
  //
  // Containment is the test rather than "did the fold change anything": a document can hold the
  // entity while selecting none of the operation's fields, yet still select server-derived ones it
  // affects (`closedAt`, `lastEditedAt`), which only a refetch reconciles.
  const targetId = operation.type === 'setFields' ? operation.id : ownerId
  const containmentPath = operation.type === 'setFields' ? applyOptions.identityPath : undefined
  const affected = targets.filter(target => {
    const base = queryClient.getQueryData(target.queryKey)
    return base !== undefined && collectIdentities(base, containmentPath).has(targetId)
  })
  if (affected.length === 0) return

  // Which targets were mid-fetch is read before cancelling, since cancelling clears it: those are
  // the ones whose in-flight read was issued against pre-mutation state and has to be reissued.
  const wasFetching = new Map(
    affected.map(target => [target, queryClient.isFetching({queryKey: target.queryKey}) > 0] as const),
  )

  await Promise.all(affected.map(target => queryClient.cancelQueries({queryKey: target.queryKey})))
  for (const target of affected) foldInto(target.queryKey)

  const revalidations = affected
    .filter(target => target.revalidate || wasFetching.get(target))
    .map(target =>
      queryClient.invalidateQueries(
        target.refetchType ? {queryKey: target.queryKey, refetchType: target.refetchType} : {queryKey: target.queryKey},
      ),
    )
  if (revalidations.length === 0) return

  await Promise.all(revalidations)
  // The refetch above is authoritative for everything except this operation, which it may have
  // been too early to include.
  for (const target of affected) foldInto(target.queryKey)

  function foldInto(queryKey: QueryKey): void {
    const base = queryClient.getQueryData(queryKey)
    if (base === undefined) return
    const next = applyOperation(base, ownerId, operation, applyOptions)
    if (next === base) return
    const state = queryClient.getQueryState(queryKey)
    queryClient.setQueryData(queryKey, next, state ? {updatedAt: state.dataUpdatedAt} : undefined)
  }
}
